import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Database, ChevronDown, ChevronUp, ExternalLink, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SectionObservationsPanel — Data-Fabric audit pack for one section.
 *
 * Reads from /api/countries/{id}/sections/{section}/observations and renders:
 *   • Run ID + provider mix + confidence breakdown chips
 *   • A scrollable table of every signal Claude saw at generation time
 *
 * Older sections that were generated before the observations table was wired
 * up (no run_id stored on the country doc) simply render an empty state with
 * a "regenerate to capture audit trail" hint.
 */
export default function SectionObservationsPanel({ countryId, sectionKey, darkMode = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!countryId || !sectionKey) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/countries/${countryId}/sections/${sectionKey}/observations`);
      setData(res.data);
    } catch {
      setData({ observations: [], summary: { count: 0 } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const obs = data?.observations || [];
  const summary = data?.summary || { count: 0, providers: [], confidence: {} };

  const panelBg = darkMode ? 'bg-[#0b1c30]/60 border-[#1a365d]' : 'bg-[#f0fdfa] border-[#a7f3d0]';
  const txtBase = darkMode ? 'text-white/90' : 'text-[#065f46]';
  const txtMuted = darkMode ? 'text-white/60' : 'text-[#047857]/70';
  const tableBg = darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-[#a7f3d0]/40';

  return (
    <div className={`mt-3 rounded-xl border ${panelBg} overflow-hidden transition-all`} data-testid={`section-observations-${sectionKey}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`section-observations-toggle-${sectionKey}`}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-black/5 transition-all ${txtBase}`}>
        <span className="flex items-center gap-2">
          <Database className="w-3.5 h-3.5 text-emerald-600" />
          Data Audit Trail
          {summary.count > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 text-[10px] font-bold normal-case tracking-normal">
              {summary.count} {summary.count === 1 ? 'observation' : 'observations'}
            </span>
          )}
          {data?.run_id && (
            <span className={`ml-2 text-[10px] font-mono normal-case tracking-normal ${txtMuted}`} title={data.run_id}>
              run · {data.run_id.slice(0, 8)}
            </span>
          )}
          <span className={`ml-2 text-[10px] font-medium normal-case tracking-normal ${txtMuted}`}>
            Every metric the AI saw, with source + fetched-at
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0">
          {loading && (
            <div className={`text-xs ${txtMuted} flex items-center gap-2 py-3`}>
              <Loader2 className="w-3 h-3 animate-spin" /> Loading audit pack…
            </div>
          )}

          {!loading && obs.length === 0 && (
            <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-emerald-100/50'} text-xs ${txtBase}`}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 opacity-60" />
              <div>
                <div className="font-semibold">No audit pack on file for this section.</div>
                <div className={`mt-0.5 ${txtMuted}`}>
                  This section was generated before the audit trail was wired up. Regenerate it to capture every datapoint with its source URL and fetched-at timestamp.
                </div>
              </div>
            </div>
          )}

          {!loading && obs.length > 0 && (
            <>
              {/* Summary chips */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-widest">
                  <ShieldCheck className="w-3 h-3" />
                  {(summary.confidence?.high || 0)} high · {(summary.confidence?.medium || 0)} medium · {(summary.confidence?.low || 0)} low
                </span>
                {(summary.providers || []).slice(0, 5).map((p) => (
                  <span key={p.provider} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold ${darkMode ? 'bg-white/10 text-white/80' : 'bg-white text-[#065f46] border border-[#a7f3d0]'}`}>
                    {p.provider}
                    <span className="opacity-60 ml-0.5">×{p.count}</span>
                  </span>
                ))}
              </div>

              {/* Observations table */}
              <div className={`max-h-[320px] overflow-y-auto rounded-lg border ${tableBg}`}>
                <table className="w-full text-[11px]">
                  <thead className={`sticky top-0 z-10 ${darkMode ? 'bg-[#0b1c30]' : 'bg-emerald-50'} ${darkMode ? 'text-white/70' : 'text-emerald-900'} font-semibold tracking-widest uppercase`}>
                    <tr>
                      <th className="text-left px-3 py-2">Metric</th>
                      <th className="text-right px-2 py-2">Value</th>
                      <th className="text-left px-2 py-2">Year</th>
                      <th className="text-left px-2 py-2">Provider</th>
                      <th className="text-left px-2 py-2">Confidence</th>
                      <th className="text-left px-2 py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody className={`${darkMode ? 'text-white/80' : 'text-[#0b1c30]'}`}>
                    {obs.map((o) => {
                      const v = o.value;
                      const valStr = typeof v === 'number'
                        ? (Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(2))
                        : String(v ?? '—');
                      const confColor = o.confidence === 'high'
                        ? 'bg-emerald-500/15 text-emerald-700'
                        : o.confidence === 'medium'
                          ? 'bg-amber-400/20 text-amber-800'
                          : 'bg-red-400/20 text-red-700';
                      return (
                        <tr key={o.id} className={`border-t ${darkMode ? 'border-white/5' : 'border-emerald-100/50'} hover:bg-emerald-50/40`}>
                          <td className="px-3 py-1.5">{o.metric_label}</td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {valStr}{o.unit && o.unit !== 'status' ? <span className="opacity-60 ml-1">{o.unit}</span> : null}
                          </td>
                          <td className="px-2 py-1.5">{o.year || (o.as_of ? o.as_of.slice(0, 7) : '—')}</td>
                          <td className="px-2 py-1.5">{o.provider}</td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${confColor}`}>
                              {o.confidence || 'medium'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            {o.source_url ? (
                              <a href={o.source_url} target="_blank" rel="noreferrer"
                                 className="inline-flex items-center gap-1 text-[#004ac6] hover:underline">
                                Open <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            ) : <span className="opacity-40">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className={`mt-2 text-[10px] ${txtMuted}`}>
                Audit pack captured at generation time. Every figure above can be independently re-fetched from the source URL — that's how reproducibility works here.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
